import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer, message, DatePicker, Table,
} from 'antd';
import axios from 'axios';
import moment from 'moment';

import { getLogColumns } from './qbAuditLog.constants';

const getDefaultRange = () => [moment().subtract(7, 'd'), moment()];
function QBAuditLogDrawer({
  visible,
  onClose,
  onLinkedRowClick,
}) {
  const [range, setRange] = useState(getDefaultRange());
  const [data, setData] = useState([]);
  const [retryingIds, setRetryingIds] = useState(new Set());

  const fetchLog = useCallback(async (currentRange) => {
    try {
      const { data: newData } = await axios.get('/quickbooks/log', {
        params: {
          startTime: currentRange[0].valueOf(),
          endTime: currentRange[1].valueOf(),
        },
      });
      setData(newData ?? []);
    } catch (err) {
      message.error('Failed to get QuickBooks log');
      setData([]);
    }
  }, []);

  useEffect(() => {
    if (visible
        && range?.length === 2
        && moment.isMoment(range[0])
        && moment.isMoment(range[1])
    ) fetchLog(range);
  }, [range, visible, fetchLog]);

  const handleRetry = useCallback(async (record) => {
    setRetryingIds((prev) => new Set([...prev, record.id]));
    try {
      await axios.post(`/quickbooks/log/${record.id}/retry`);
      message.success('Time entry sync retried');
      await fetchLog(range);
    } catch (err) {
      message.error('Failed to retry time entry sync');
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  }, [range, fetchLog]);

  const columns = useMemo(
    () => getLogColumns(handleRetry, retryingIds),
    [handleRetry, retryingIds],
  );

  const expandedRowRender = useCallback((record) => {
    const body = Array.isArray(record.response)
      ? (
        record.response?.map((res) => (
          <div>- {res}</div>
        )) ?? ''
      ) : record.response ?? '';
    return (
      <p style={{ margin: 0, marginLeft: 70 }}>
        {body}
      </p>
    );
  }, []);

  return (
    <Drawer
      title="QuickBooks Log"
      visible={visible}
      width={1300}
      onClose={onClose}
      style={{
        height: '100%',
      }}
      bodyStyle={{ padding: 0 }}
    >
      <DatePicker.RangePicker
        style={{ width: 250, margin: 20 }}
        format="MMM Do YY"
        disabledDate={(current) => current > moment().endOf('day')}
        value={range}
        onChange={setRange}
        getPopupContainer={(node) => (node ? node.parentNode : document.body)}
      />
      <Table
        rowKey="id"
        dataSource={data}
        columns={columns}
        pagination={{
          hideOnSinglePage: true,
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.('.ant-table-row-expand-icon-cell')) return;
            if (record.linkedEntity && onLinkedRowClick) {
              onLinkedRowClick(record);
            }
          },
          style: record.linkedEntity ? { cursor: 'pointer' } : undefined,
        })}
        expandable={{
          expandedRowRender,
          rowExpandable: (record) => (
            record.status !== 'Succeeded'
            && !!record.response
          ),
        }}
      />
    </Drawer>
  );
}

QBAuditLogDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  onLinkedRowClick: PropTypes.func,
};

QBAuditLogDrawer.defaultProps = {
  visible: false,
  onLinkedRowClick: undefined,
};

export default QBAuditLogDrawer;
